import React, { useCallback, useEffect, useRef } from 'react';

const SIDEBAR_DRAG_THRESHOLD_PX = 8;

type UseMobileSidebarBottomSheetDragProps = {
  enabled: boolean;
  peekRef: React.MutableRefObject<number>;
  applyPeek: (px: number) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
};

export function useMobileSidebarBottomSheetDrag({
  enabled,
  peekRef,
  applyPeek,
  containerRef
}: UseMobileSidebarBottomSheetDragProps) {
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef<{
    mouseX: number;
    mouseY: number;
    pointerId: number;
    target: HTMLDivElement;
  } | null>(null);
  const dragPeekStartRef = useRef({ mouseY: 0, peek: 0 });

  const endDrag = useCallback(() => {
    const dragStart = dragStartRef.current;
    if (containerRef.current && isDraggingRef.current) {
      containerRef.current.classList.remove('b-sidebar-dragging');
    }
    dragStart?.target.releasePointerCapture?.(dragStart.pointerId);

    dragStartRef.current = null;
    isDraggingRef.current = false;
  }, [containerRef]);

  const onSidebarHandlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!enabled) return;

      dragStartRef.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        pointerId: e.pointerId,
        target: e.currentTarget
      };
    },
    [enabled]
  );

  useEffect(() => {
    if (!enabled) return undefined;

    const onPointerMove = (e: PointerEvent) => {
      const dragStart = dragStartRef.current;
      if (!dragStart) return;
      if (dragStart.pointerId !== e.pointerId) return;

      if (!isDraggingRef.current) {
        const deltaX = e.clientX - dragStart.mouseX;
        const deltaY = e.clientY - dragStart.mouseY;
        if (Math.hypot(deltaX, deltaY) < SIDEBAR_DRAG_THRESHOLD_PX) return;

        isDraggingRef.current = true;
        dragPeekStartRef.current = {
          mouseY: e.clientY,
          peek: peekRef.current
        };
        containerRef.current?.classList.add('b-sidebar-dragging');
        dragStart.target.setPointerCapture?.(e.pointerId);
      }

      const { mouseY, peek } = dragPeekStartRef.current;
      applyPeek(peek + mouseY - e.clientY);
    };

    const onPointerUp = (e: PointerEvent) => {
      const dragStart = dragStartRef.current;
      if (!dragStart) return;
      if (dragStart.pointerId !== e.pointerId) return;

      if (isDraggingRef.current) {
        endDrag();
        return;
      }

      dragStartRef.current = null;
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerUp);

    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
    };
  }, [enabled, applyPeek, peekRef, containerRef, endDrag]);

  return { onSidebarHandlePointerDown };
}
